import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingContext, M_Table, NavigateContext, BufferService } from 'src/M_Components'
import { MovementCheckDemurrage } from '../CONTAINERCONTROL/movementCheckDemurrage'
import { MovementGroupDialog } from './movementGroupDialog'
import { DataTable } from 'primereact/datatable/datatable.cjs.js'
import { Column } from 'primereact/column/column.cjs.js'

export const LastMovementList = ({ roles }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)

    const [showCheckDemurrage, setShowCheckDemurrage] = useState(false)
    const [selectedMovements, setSelectedMovements] = useState([])
    const [refresh, setRefresh] = useState(false)
    const [equipmentPanels, setEquipmentPanels] = useState(new Map())
    const [showGroupDialog, setShowGroupDialog] = useState(false)

    // Context menu state — position and the row it was triggered on
    const [contextMenu, setContextMenu] = useState(null)
    const [contextRow, setContextRow] = useState(null)

    const movementService = new BufferService('Movement', 'secondary')

    const newRoute = (url, operation, label, row) => {
        if (operation === 'Edit' || operation === 'Copy') {
            if (row) {
                sessionStorage.setItem('editingMovement', JSON.stringify(row))
                sessionStorage.setItem('operationType', operation)
            } else {
                return
            }
        }
        if (operation === 'Create') ctx.setPassValues({ Operation: operation })
        ctx2.newTab(url, label)
    }

    const handleDelete = async (rows) => {
        if (!rows || rows.length === 0) {
            ctx.toastMessages('error', '', t('SelectedLine'))
            return
        }
        ctx.setIsLoad(true)
        for (const row of rows) {
            const res = await movementService.delete(row.Id)
            if (res?.name === 'AxiosError') {
                ctx.setIsLoad(false)
                const message = res.response?.data?.detail
                    || res.response?.data?.Message
                    || res.message
                    || t('Error')
                ctx.toastMessages('error', message, '')
                return
            }
        }
        ctx.setIsLoad(false)
        ctx.toastMessages('success', '', t('RemoveLine'))
        setRefresh(true)
    }

    const handleOpenCheckDemurrage = (rows) => {
        if (!rows || rows.length === 0) {
            ctx.toastMessages('error', '', t('Please select at least one movement'))
            return
        }
        setSelectedMovements(rows)
        setShowCheckDemurrage(true)
    }

    const handleChangeGroupMovement = (rows) => {
        if (!rows || rows.length < 2) {
            ctx.toastMessages('error', '', t('Please select at least 2 movement rows for group update'))
            return
        }
        setSelectedMovements(rows)
        setShowGroupDialog(true)
    }

    const handleRowSelect = (rows) => {
        if (!rows || rows.length === 0) {
            setEquipmentPanels(new Map())
            setSelectedMovements([])
            return
        }

        setSelectedMovements(rows)

        const currentIds = new Set(rows.map(r => r.EquipmentId))

        setEquipmentPanels(prev => {
            const next = new Map(prev)
            for (const id of next.keys()) {
                if (!currentIds.has(id)) next.delete(id)
            }
            return next
        })

        rows.forEach(row => {
            setEquipmentPanels(prev => {
                if (prev.has(row.EquipmentId)) return prev

                const next = new Map(prev)
                next.set(row.EquipmentId, { row, history: [], loading: true })

                const svc = new BufferService('Movement', 'secondary')
                svc.getId(`byequipment/${row.EquipmentId}`).then(data => {
                    if (data?.name === 'AxiosError') {
                        ctx.toastMessages('error', data.message, '')
                        setEquipmentPanels(p => {
                            const updated = new Map(p)
                            updated.set(row.EquipmentId, { row, history: [], loading: false })
                            return updated
                        })
                        return
                    }
                    setEquipmentPanels(p => {
                        const updated = new Map(p)
                        updated.set(row.EquipmentId, {
                            row,
                            history: Array.isArray(data) ? data : [],
                            loading: false
                        })
                        return updated
                    })
                })

                return next
            })
        })
    }

    const closePanel = (equipmentId) => {
        setEquipmentPanels(prev => {
            const next = new Map(prev)
            next.delete(equipmentId)
            return next
        })
    }

    const getEquipmentLabel = (row) =>
        `${row.EquipmentPrefix ?? ''}${row.EquipmentNumber ?? ''}-${row.EquipmentCheckDigit ?? ''}`

    const handleGroupSuccess = () => {
        setRefresh(true)
    }

    // Opens the context menu at the cursor position for the right-clicked history row
    const handleContextMenu = (e, row) => {
        e.preventDefault()
        setContextRow(row)
        setContextMenu({ x: e.clientX, y: e.clientY })
    }

    const closeContextMenu = () => {
        setContextMenu(null)
        setContextRow(null)
    }

    // Check Demurrage triggered from context menu on a single history row
    const handleContextCheckDemurrage = () => {
        if (contextRow) {
            handleOpenCheckDemurrage([contextRow])
        }
        closeContextMenu()
    }

    return (
        <>
            <M_Table
                entityName={'Movement/lastmovements'}
                api='secondary'
                showTitle={true}
                title={'Last Movements'}
                roleName={'usersGroups'}
                rolePermissions={roles}
                refresh={refresh}
                setRefresh={setRefresh}
                replaceFunctions={new Map([
                    ['Create', (row) => newRoute('containerControl/lastMovementCrud', 'Create', t('Add') + ' ' + t('Movement'), row)],
                    ['Edit',   (row) => newRoute('containerControl/lastMovementCrud', 'Edit',   t('Edit') + ' ' + t('Movement'), row)],
                    ['Copy',   (row) => newRoute('containerControl/lastMovementCrud', 'Copy',   t('Copy') + ' ' + t('Movement'), row)],
                    ['Delete', (rows) => handleDelete(rows)],
                    ['CheckDemurrage', (rows) => handleOpenCheckDemurrage(rows)],
                    ['ChangeGroupMovement', (rows) => handleChangeGroupMovement(rows)],
                ])}
                onPassLine={handleRowSelect}
            />

            {[...equipmentPanels.values()].map(({ row, history, loading }) => (
                <div
                    key={row.EquipmentId}
                    style={{
                        margin: '16px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                >
                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #dee2e6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                            {t('Movement History')} — {getEquipmentLabel(row)}
                        </span>
                        <button
                            onClick={() => closePanel(row.EquipmentId)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#6c757d' }}
                        >
                            
                        </button>
                    </div>
                    {loading ? (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d' }}>
                            {t('Loading')}...
                        </div>
                    ) : (
                        <DataTable
                            value={history}
                            size="small"
                            stripedRows
                            emptyMessage={t('No movements found')}
                            style={{ fontSize: '0.875rem' }}
                            // Right-click on any history row opens the context menu
                            onContextMenu={(e) => handleContextMenu(e.originalEvent, e.data)}
                            contextMenuSelection={contextRow}
                        >
                            <Column field="Date" header={t('Date')} body={(r) => r.Date ? r.Date.replace('T', '     |     ').substring(0, 26) : ''} style={{ width: '200px' }} />
                            <Column field="MovementTypeCode" header={t('Move')} style={{ width: '80px' }} />
                            <Column field="Status" header={t('Status')} />
                            <Column field="EquipmentServiceCode" header={t('Service')} style={{ width: '90px' }} />
                            <Column field="EquipmentConditionCode" header={t('Condition')} style={{ width: '100px' }} />
                            <Column field="MovementFromCode" header={t('From')} style={{ width: '100px' }} />
                            <Column field="MovementToCode" header={t('To')} style={{ width: '100px' }} />
                            <Column field="MovementFinalCode" header={t('Final')} style={{ width: '100px' }} />
                            <Column field="TransportName" header={t('Transport')} />
                            <Column field="VoyageNumber" header={t('Voyage')} style={{ width: '90px' }} />
                            <Column field="Comments" header={t('Comments')} />
                        </DataTable>
                    )}
                </div>
            ))}

            {/*
                Context menu — appears at cursor position when right-clicking a history row.
                Dismissed by clicking outside or selecting an option.
                Currently exposes Check Demurrage for a single history row.
            */}
            {contextMenu && (
                <div
                    style={{
                        position: 'fixed',
                        top: contextMenu.y,
                        left: contextMenu.x,
                        backgroundColor: 'white',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 9999,
                        minWidth: '160px'
                    }}
                    // Close when mouse leaves the menu entirely
                    onMouseLeave={closeContextMenu}
                >
                    <div
                        style={{
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            color: '#495057'
                        }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={handleContextCheckDemurrage}
                    >
                        {t('Check Demurrage')}
                    </div>
                </div>
            )}

            <MovementGroupDialog
                open={showGroupDialog}
                setOpen={setShowGroupDialog}
                mode='update'
                selectedRows={selectedMovements}
                onSuccess={handleGroupSuccess}
            />

            <MovementCheckDemurrage
                open={showCheckDemurrage}
                setOpen={setShowCheckDemurrage}
                selectedMovements={selectedMovements}
            />
        </>
    )
}